import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, ArrowRight, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { ErrorBanner } from '../components/shared/ErrorBanner';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isAuthenticated) navigate('/');
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    emailRef.current?.focus();
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError('Invalid email or password.');
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = email.trim() !== '' && password.trim() !== '';

  return (
    <div
      style={{
        minHeight: '100vh',
        background:
          'radial-gradient(circle at 20% 0%, color-mix(in oklab, var(--accent) 8%, transparent), transparent 50%), ' +
          'radial-gradient(circle at 80% 100%, color-mix(in oklab, var(--accent) 6%, transparent), transparent 55%), ' +
          'var(--bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div style={{ width: '100%', maxWidth: 400, display: 'flex', flexDirection: 'column', gap: 22 }}>
        {/* Brand */}
        <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column', gap: 12 }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 'var(--r-3)',
              background: 'var(--accent)',
              color: 'var(--on-accent)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 600,
              fontSize: 26,
              letterSpacing: '-0.02em',
              boxShadow: '0 4px 14px var(--accent-ring)',
            }}
            aria-hidden="true"
          >
            S
          </div>
          <div
            style={{
              fontSize: 11,
              color: 'var(--fg-3)',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              fontWeight: 500,
            }}
          >
            Seeker · admin console
          </div>
        </div>

        <div className="hf-card" style={{ boxShadow: 'var(--shadow-md)' }}>
          <div className="hf-card-body" style={{ padding: 24 }}>
            <h1
              style={{
                margin: 0,
                fontSize: 22,
                fontWeight: 600,
                letterSpacing: '-0.02em',
                color: 'var(--fg-1)',
              }}
            >
              Welcome back
            </h1>
            <p style={{ margin: '4px 0 18px', fontSize: 13, color: 'var(--fg-3)' }}>
              Sign in to manage models, content & analytics.
            </p>

            {error && (
              <div style={{ marginBottom: 14 }}>
                <ErrorBanner kind="danger" message={error} inline />
              </div>
            )}

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <div className="hf-label-row">
                  <span className="hf-label">Email</span>
                </div>
                <div className="hf-input">
                  <span className="hf-icon-l"><Mail size={14} /></span>
                  <input
                    ref={emailRef}
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="admin@onlyseeker.com"
                    autoComplete="email"
                    aria-required="true"
                    aria-invalid={error ? true : undefined}
                  />
                </div>
              </div>

              <div>
                <div className="hf-label-row">
                  <span className="hf-label">Password</span>
                  <a
                    href="#"
                    style={{ fontSize: 11.5, color: 'var(--fg-3)', textDecoration: 'none', marginTop: 0 }}
                    onClick={(e) => {
                      e.preventDefault();
                      alert('Contact an administrator for password reset.');
                    }}
                  >
                    Forgot?
                  </a>
                </div>
                <div className="hf-input">
                  <span className="hf-icon-l"><Lock size={14} /></span>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    aria-required="true"
                    aria-invalid={error ? true : undefined}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      color: 'var(--fg-3)',
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>

              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 12.5,
                  color: 'var(--fg-2)',
                  marginTop: 4,
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  style={{ accentColor: 'var(--accent)' }}
                />
                Keep me signed in
              </label>

              <button
                type="submit"
                disabled={loading || !isFormValid}
                className="hf-btn primary"
                style={{ width: '100%', padding: '10px 13px', marginTop: 6, fontSize: 13.5 }}
              >
                {loading
                  ? <><Loader2 size={14} className="spin" /> Signing in…</>
                  : <>Sign in <ArrowRight size={14} /></>}
              </button>
            </form>
          </div>
        </div>

        <p style={{ textAlign: 'center', fontSize: 11, color: 'var(--fg-3)', margin: 0 }}>
          © {new Date().getFullYear()} OnlySeeker · internal use only
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
